import { Badge, Card, Clickable } from '@warp-ds/react';
import { trackFhhItemCarouselClick } from 'shared/search/common/tracking';
import { FavoriteButton } from './FavoriteButton';
import styles from './FeaturedAdsCarousel.module.css';

export type FeaturedAd = {
    itemId: number;
    heading: string;
    imageUrl: string;
    locality: string | null;
    pricePerNight: number | null;
    availableNightsNext60: number;
    noOfBedrooms?: number;
    sleepingCapacity?: number;
};

type FeaturedAdCardProps = {
    ad: FeaturedAd;
    loginId?: string | null;
};

const makeSrcSet = (url: string) => `${url.replace('/default/', '/640x480c/')} 640w, ${url.replace('/default/', '/320x240c/')} 320w`;

const applyImageRule = (url: string, rule: string) => url.replace('/default/', rule);

const joinWithBullets = (...items: (string | false | null | undefined)[]) => {
    const filtered = items.filter(Boolean) as string[];
    return filtered.map((item, _i, arr) => (
        <span key={item}>
            {item}
            {item !== arr[arr.length - 1] && (
                <span className="text-gray-500" aria-hidden="true">
                    {' • '}
                </span>
            )}
        </span>
    ));
};

export const FeaturedAdCard = ({ ad, loginId }: FeaturedAdCardProps) => {
    const adUrl = `/reise/feriehus-hytteutleie/ad.html?finnkode=${ad.itemId}`;

    return (
        <div className={`${styles.card} relative`}>
            <article>
                <Card>
                    <img
                        className="h-144 w-full object-cover"
                        src={applyImageRule(ad.imageUrl, '/480w/')}
                        srcSet={makeSrcSet(ad.imageUrl)}
                        alt=""
                        sizes="(min-width: 768px) 280px, 80vw"
                        loading="lazy"
                    />
                    <Badge variant="info" position="top-left" className="px-8 gap-4">
                        Enkel bestilling
                    </Badge>
                    <div className="p-16">
                        {ad.locality && <p className="text-12 text-gray-500 truncate mb-0">{ad.locality}</p>}
                        <h4 className="truncate text-16 font-normal text-gray-800 mt-2">
                            <Clickable
                                href={adUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackFhhItemCarouselClick(String(ad.itemId), ad.heading)}
                                className="text-current hover:no-underline focus:no-underline">
                                {ad.heading}
                            </Clickable>
                        </h4>
                        {ad.pricePerNight != null && (
                            <p className="text-16 font-bold mt-8 mb-0">Fra {ad.pricePerNight.toLocaleString('nb-NO')} kr / natt</p>
                        )}
                        <p className="text-14 text-gray-500 mt-4 mb-0">
                            {joinWithBullets(
                                ad.noOfBedrooms != null && `${ad.noOfBedrooms} soverom`,
                                ad.sleepingCapacity != null && `${ad.sleepingCapacity} sengeplasser`,
                                'Privat',
                            )}
                        </p>
                    </div>
                </Card>
            </article>
            <FavoriteButton
                itemId={ad.itemId}
                variant="heart-on-image"
                itemType="Ad"
                loginId={loginId}
                wrapperClassName="absolute top-0 right-0 m-4"
            />
        </div>
    );
};
